import { CloseCommand } from "appworks/commands/comms/close-command";
import { Components } from "appworks/components/components";
import { gameState } from "appworks/model/game-state";
import { Gameplay } from "appworks/model/gameplay/gameplay";
import { Record } from "appworks/model/gameplay/records/record";
import { Services } from "appworks/services/services";
import { SuperState } from "appworks/state-machine/states/superstate";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { Contract } from "appworks/utils/contracts/contract";
import { SignalBinding } from "signals";
import { CelebrateWinComponent } from "slotworks/components/celebrate-win/celebrate-win-component";
import { FreespinRecord } from "slotworks/model/gameplay/records/freespin-record";
import RespinResult from "slotworks/model/gameplay/records/results/respin-result";
import { SpinRecord } from "slotworks/model/gameplay/records/spin-record";

export interface WinCycleConfig {
    holdSpinDelay: number;
}
export class WinCycle extends SuperState {

    protected config: WinCycleConfig = {
        holdSpinDelay: 0
    };
    protected readyToExit: boolean;
    protected onReadyToExit: () => void;
    protected signalBindings: SignalBinding[] = [];
    protected isFreeSpins: boolean;
    protected collapsed: boolean;

    constructor(config?: Partial<WinCycleConfig>) {
        super();

        if (config) {
            this.config = { ...this.config, ...config };
        }
    }

    public onEnter() {
        this.signalBindings.push(Services.bet.onBetChange.add(() => this.collapse()));
        this.signalBindings.push(Services.autoplay.onConfirm.add(() => this.collapse()));

        const gameplay = gameState.getCurrentGame();

        this.readyToExit = false;
        this.onReadyToExit = null;
        this.isFreeSpins = false;
        this.collapsed = false;

        const recordWin = this.getRecordWin();
        const totalWin = this.getTotalWin();

        const record = gameplay.getCurrentRecord();

        if (gameplay.isComplete() && !gameplay.isRecovered) {
            Services.jurisdiction.winningsReceived(totalWin);
        }

        if (this.shouldCloseGame(record, gameplay)) {
            uiFlags.set(UIFlag.GAME_IN_PROGRESS, false);
            uiFlags.set(UIFlag.AWAITING_RESPONSE, true);

            this.closeGame().then(() => {
                Services.transaction.setWinnings(recordWin, totalWin).execute();

                if (!this.isFreeSpins) {
                    this.gameComplete();
                }

                uiFlags.set(UIFlag.AWAITING_RESPONSE, false);
                if (!Services.autoplay.isAutoPlaying()) {
                    uiFlags.set(UIFlag.IDLE, true);
                }

                this.readyToExit = true;

                if (this.onReadyToExit) {
                    this.onReadyToExit();
                }
            });
        } else {
            Services.transaction.setWinnings(recordWin, totalWin).execute();
            this.readyToExit = true;
        }
    }

    public collapse() {
        this.collapsed = true;

        Services.transaction.setTotalWin(0);

        if (this.readyToExit) {
            super.collapse();
        } else {
            this.onReadyToExit = () => super.collapse();
        }
    }

    public onSubStateSkip() {
        uiFlags.set(UIFlag.IDLE, false);
        uiFlags.set(UIFlag.GAME_IN_PROGRESS, true);

        if (this.readyToExit) {
            this.confirmSkip();
        } else {
            this.onReadyToExit = () => this.confirmSkip();
        }
    }

    public onSubStateComplete() {
        if (this.readyToExit) {
            this.confirmComplete();
        } else {
            this.onReadyToExit = () => this.confirmComplete();
            this.repeat();
        }
    }

    public onSuperStateExit() {
        if (this.readyToExit) {
            this.confirmExit();
        } else {
            this.onReadyToExit = () => this.confirmExit();
        }
    }

    public onExit() {
        if (!this.collapsed) {
            uiFlags.closeMenus();
        }

        if (!this.isFreeSpins) {
            Services.transaction.setBalance(gameState.getCurrentGame().balance);
        }

        Components.get(CelebrateWinComponent).cleanup();
        Components.get(CelebrateWinComponent).hide();

        for (const binding of this.signalBindings) {
            binding.detach();
        }
        this.signalBindings = [];
    }

    protected getTotalWin() {
        const gameplay = gameState.getCurrentGame();
        return gameplay.getTotalWin();
    }

    protected getRecordWin() {
        const gameplay = gameState.getCurrentGame();
        return gameplay.getCurrentRecord().cashWon;
    }

    protected closeGame(): Contract<void> {
        return CloseCommand();
    }

    protected gameComplete() {
        Services.transaction.setBalance(gameState.getCurrentGame().balance);
    }

    protected shouldCloseGame(record: Record, gameplay: Gameplay) {
        let closeGame = true;

        if (record instanceof SpinRecord) {
            if (record.getFreespinsWon() > 0 || record.isBonusTriggered()) {
                closeGame = false;
            }
        }

        const respinResult = gameplay.getLatestResultOfType(RespinResult);
        if (respinResult && respinResult.respinsRemaining) {
            closeGame = false;
        }

        if (record instanceof FreespinRecord) {
            this.isFreeSpins = true;
            closeGame = false;
        } else if (gameplay.isComplete()) {
            closeGame = true;
        }

        return closeGame;
    }
}
